import {
    supabase,
    isSupabaseConfigured,
    requireSupabase,
    ensureValidSession,
} from "@/lib/supabase";
import { logger } from "@/lib/logger";
import type {
    ScheduleItem,
    AppointmentStatus,
    CalendarResource,
    ResourceScheduleItem,
} from "@/types/appointment";
import { startOfDay, endOfDay } from "date-fns";

export class AppointmentServiceError extends Error {
    code: string;

    constructor(message: string, code = "APPOINTMENT_ERROR") {
        super(message);
        this.name = "AppointmentServiceError";
        this.code = code;
    }
}

type WaitingRoomStatus =
    | "checked_in"
    | "waiting"
    | "called"
    | "in_treatment"
    | "completed"
    | "no_show"
    | "cancelled";

const WAITING_ROOM_STATUS_BY_APPOINTMENT_STATUS: Partial<
    Record<AppointmentStatus, WaitingRoomStatus>
> = {
    arrived: "checked_in",
    checked_in: "checked_in",
    waiting: "waiting",
    in_treatment: "in_treatment",
    completed: "completed",
    cancelled: "cancelled",
    no_show: "no_show",
};

function coerceCalendarDateInput(date: string | Date): Date {
    if (date instanceof Date) {
        return date;
    }

    const match = date.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (match) {
        const [, year, month, day] = match;
        return new Date(Number(year), Number(month) - 1, Number(day), 12, 0, 0, 0);
    }

    return new Date(date);
}

function getWaitingRoomStatusForAppointmentStatus(
    status: AppointmentStatus,
): WaitingRoomStatus | null {
    return WAITING_ROOM_STATUS_BY_APPOINTMENT_STATUS[status] ?? null;
}

function getAppointmentPatientName(row: Record<string, unknown>): string {
    const patientsJoined = row.patients as unknown;
    const patientsObj = Array.isArray(patientsJoined)
        ? ((patientsJoined[0] as Record<string, unknown> | undefined) ?? null)
        : ((patientsJoined as Record<string, unknown> | null) ?? null);
    const patientName = patientsObj
        ? `${patientsObj.first_name || ""} ${patientsObj.last_name || ""}`.trim()
        : "";

    return patientName || "Patient";
}

async function syncWaitingRoomStatus(
    appointmentId: string,
    status: AppointmentStatus,
): Promise<void> {
    const sb = requireSupabase();
    const waitingRoomStatus = getWaitingRoomStatusForAppointmentStatus(status);

    if (!waitingRoomStatus) {
        return;
    }

    const { data: existingSession, error: existingSessionError } = await sb
        .from("practice_waiting_sessions")
        .select("id")
        .eq("appointment_id", appointmentId)
        .maybeSingle();

    if (existingSessionError) {
        throw existingSessionError;
    }

    if (existingSession?.id) {
        const { error: updateWaitingSessionError } = await sb
            .from("practice_waiting_sessions")
            .update({ status: waitingRoomStatus })
            .eq("id", existingSession.id);

        if (updateWaitingSessionError) {
            throw updateWaitingSessionError;
        }

        return;
    }

    const { data: appointment, error: appointmentError } = await sb
        .from("appointments")
        .select("practice_id, patient_id, doctor_id, patients(first_name, last_name)")
        .eq("id", appointmentId)
        .maybeSingle();

    if (appointmentError) {
        throw appointmentError;
    }

    if (!appointment?.practice_id) {
        return;
    }

    const { data: defaultZone, error: zoneError } = await sb
        .from("practice_waiting_zones")
        .select("id")
        .eq("practice_id", appointment.practice_id)
        .eq("is_default", true)
        .limit(1)
        .maybeSingle();

    if (zoneError) {
        throw zoneError;
    }

    const { error: insertWaitingSessionError } = await sb.from("practice_waiting_sessions").insert({
        practice_id: appointment.practice_id,
        patient_id: (appointment.patient_id as string | null | undefined) ?? null,
        appointment_id: appointmentId,
        patient_name: getAppointmentPatientName(appointment as Record<string, unknown>),
        status: waitingRoomStatus,
        queue_number: Math.floor(Math.random() * 1000),
        queue_position: 1,
        priority: 0,
        check_in_time: new Date().toISOString(),
        check_in_method: "reception",
        urgency: "normal",
        assigned_doctor_id: (appointment.doctor_id as string | null | undefined) ?? null,
        zone_id: defaultZone?.id ?? null,
    });

    if (insertWaitingSessionError) {
        throw insertWaitingSessionError;
    }
}

/**
 * Get schedule items for a date (practice calendar)
 */
export async function getScheduleByDate(
    practiceId: string,
    date: string | Date,
    options?: { doctorId?: string },
): Promise<ScheduleItem[]> {
    if (!isSupabaseConfigured()) return [];

    const dateObj = coerceCalendarDateInput(date);
    const dayStart = startOfDay(dateObj).toISOString();
    const dayEnd = endOfDay(dateObj).toISOString();

    // Try unified view first (includes guest appointments)
    try {
        const { data } = await supabase
            .from("v_practice_calendar")
            .select("*")
            .eq("practice_id", practiceId)
            .gte("start_time", dayStart)
            .lte("start_time", dayEnd)
            .order("start_time", { ascending: true });

        if (data && data.length > 0) {
            const mapped = data.map(mapScheduleItem);
            return options?.doctorId
                ? mapped.filter((s) => s.doctorId === options.doctorId)
                : mapped;
        }
    } catch {
        // View not available, use fallback
    }

    // Fallback: appointments table with patient join (LEFT JOIN to include walk-in/guest)
    const { data, error } = await supabase
        .from("appointments")
        .select(
            `
      id, patient_id, doctor_id, start_time, end_time,
      appointment_type, status, reason, notes,
      guest_patient_name, walk_in_name,
      patients (first_name, last_name)
    `,
        )
        .eq("practice_id", practiceId)
        .gte("start_time", dayStart)
        .lte("start_time", dayEnd)
        .order("start_time", { ascending: true });

    if (error) throw new Error(`Termine laden fehlgeschlagen: ${error.message}`);

    const registeredAppointments = (data || []).map((row): ScheduleItem => {
        const r = row as Record<string, unknown>;
        return {
            id: r.id as string,
            patientId: r.patient_id as string,
            patientName: getPatientDisplayName(r),
            startTime: r.start_time as string,
            endTime: r.end_time as string,
            appointmentType: r.appointment_type as string,
            status: r.status as AppointmentStatus,
            reason: r.reason as string | null,
            notes: r.notes as string | undefined,
            doctorId: (r.doctor_id as string | undefined) || undefined,
        };
    });

    const guestAppointments = await getGuestScheduleItemsByDate(practiceId, dateObj, dayStart, dayEnd);
    const merged = [...registeredAppointments, ...guestAppointments].sort((left, right) => {
        const leftTime = left.startTime ? new Date(left.startTime).getTime() : 0;
        const rightTime = right.startTime ? new Date(right.startTime).getTime() : 0;
        return leftTime - rightTime;
    });

    return options?.doctorId
        ? merged.filter((s) => s.doctorId === options.doctorId)
        : merged;
}

async function getGuestScheduleItemsByDate(
    practiceId: string,
    dateObj: Date,
    dayStart: string,
    dayEnd: string,
): Promise<ScheduleItem[]> {
    const currentGuestResult = await supabase
        .from("guest_appointments")
        .select(
            `
      id,
      patient_first_name,
      patient_last_name,
      patient_phone,
      patient_email,
      start_time,
      end_time,
      service_name,
      status,
      notes,
      confirmation_code,
      insurance_type
    `,
        )
        .eq("practice_id", practiceId)
        .gte("start_time", dayStart)
        .lte("start_time", dayEnd)
        .order("start_time", { ascending: true });

    if (!currentGuestResult.error) {
        return (currentGuestResult.data || []).map((row) =>
            mapCurrentGuestScheduleItem(row as Record<string, unknown>),
        );
    }

    const legacyDateKey = formatCalendarDateKey(dateObj);
    const legacyGuestResult = await supabase
        .from("guest_appointments")
        .select(
            `
      id,
      guest_name,
      guest_phone,
      guest_email,
      appointment_date,
      appointment_time,
      duration_minutes,
      status,
      confirmation_code,
      insurance_type,
      notes
    `,
        )
        .eq("practice_id", practiceId)
        .eq("appointment_date", legacyDateKey)
        .order("appointment_time", { ascending: true });

    if (legacyGuestResult.error) {
        logger.warn(
            "[Appointments] guest_appointments fallback fehlgeschlagen:",
            legacyGuestResult.error.message,
            legacyGuestResult.error.code,
        );
        return [];
    }

    return (legacyGuestResult.data || []).map((row) =>
        mapLegacyGuestScheduleItem(row as Record<string, unknown>),
    );
}

function mapCurrentGuestScheduleItem(row: Record<string, unknown>): ScheduleItem {
    const patientName = [row.patient_first_name, row.patient_last_name]
        .filter((value): value is string => typeof value === "string" && value.trim().length > 0)
        .join(" ")
        .trim();

    return {
        id: row.id as string,
        patientId: "",
        patientName: patientName || "Gasttermin",
        startTime: (row.start_time as string) || "",
        endTime: (row.end_time as string) || (row.start_time as string) || "",
        appointmentType: (row.service_name as string) || "Termin",
        status: ((row.status as AppointmentStatus | undefined) || "scheduled"),
        reason: null,
        serviceName: row.service_name as string | undefined,
        notes: row.notes as string | undefined,
        insuranceType: row.insurance_type as string | undefined,
        bookingSource: "guest",
        confirmationCode: row.confirmation_code as string | undefined,
        patientPhone: row.patient_phone as string | undefined,
        patientEmail: row.patient_email as string | undefined,
    };
}

function mapLegacyGuestScheduleItem(row: Record<string, unknown>): ScheduleItem {
    const startTime = buildLegacyGuestStartTime(row);
    const durationMinutes =
        typeof row.duration_minutes === "number" ? row.duration_minutes : Number(row.duration_minutes) || 0;
    const endTime =
        startTime && durationMinutes > 0
            ? new Date(new Date(startTime).getTime() + durationMinutes * 60_000).toISOString()
            : startTime;

    return {
        id: row.id as string,
        patientId: "",
        patientName:
            (typeof row.guest_name === "string" && row.guest_name.trim().length > 0
                ? row.guest_name.trim()
                : "Gasttermin"),
        startTime,
        endTime,
        appointmentType: "Termin",
        status: ((row.status as AppointmentStatus | undefined) || "scheduled"),
        reason: null,
        notes: row.notes as string | undefined,
        insuranceType: row.insurance_type as string | undefined,
        bookingSource: "guest",
        confirmationCode: row.confirmation_code as string | undefined,
        patientPhone: row.guest_phone as string | undefined,
        patientEmail: row.guest_email as string | undefined,
    };
}

function buildLegacyGuestStartTime(row: Record<string, unknown>): string {
    const appointmentDate = typeof row.appointment_date === "string" ? row.appointment_date : "";
    const appointmentTime = typeof row.appointment_time === "string" ? row.appointment_time : "";

    if (!appointmentDate || !appointmentTime) {
        return "";
    }

    return `${appointmentDate}T${appointmentTime}`;
}

function formatCalendarDateKey(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

/**
 * Get today's appointment count
 */
export async function getTodayAppointmentCount(practiceId: string): Promise<number> {
    if (!isSupabaseConfigured()) return 0;

    const dayStart = startOfDay(new Date()).toISOString();
    const dayEnd = endOfDay(new Date()).toISOString();

    const { count } = await supabase
        .from("appointments")
        .select("id", { count: "exact", head: true })
        .eq("practice_id", practiceId)
        .gte("start_time", dayStart)
        .lte("start_time", dayEnd)
        .not("status", "eq", "cancelled");

    return count || 0;
}

/**
 * Get count of patients currently in the waiting room (checked_in / waiting)
 */
export async function getWaitingPatientCount(practiceId: string): Promise<number> {
    if (!isSupabaseConfigured()) return 0;

    const dayStart = startOfDay(new Date()).toISOString();

    const { count } = await supabase
        .from("practice_waiting_sessions")
        .select("id", { count: "exact", head: true })
        .eq("practice_id", practiceId)
        .gte("check_in_time", dayStart)
        .in("status", ["checked_in", "called", "in_treatment"]);

    return count || 0;
}

/**
 * Get appointments for a specific patient
 */
export async function getAppointmentsByPatient(
    practiceId: string,
    patientId: string,
): Promise<ScheduleItem[]> {
    if (!isSupabaseConfigured()) return [];

    const { data, error } = await supabase
        .from("appointments")
        .select(
            `
      id, patient_id, doctor_id, start_time, end_time,
      appointment_type, status, reason, notes,
      patients!inner (first_name, last_name)
    `,
        )
        .eq("practice_id", practiceId)
        .eq("patient_id", patientId)
        .order("start_time", { ascending: false })
        .limit(50);

    if (error || !data) return [];

    return data.map((row): ScheduleItem => {
        const r = row as Record<string, unknown>;
        return {
            id: r.id as string,
            patientId: r.patient_id as string,
            patientName: getPatientDisplayName(r),
            startTime: r.start_time as string,
            endTime: r.end_time as string,
            appointmentType: r.appointment_type as string,
            status: r.status as AppointmentStatus,
            reason: r.reason as string | null,
            notes: r.notes as string | undefined,
        };
    });
}

/**
 * Update appointment status
 */
export async function updateAppointmentStatus(
    appointmentId: string,
    status: AppointmentStatus,
    practiceId?: string,
): Promise<void> {
    const sb = requireSupabase();
    const token = await ensureValidSession();
    if (!token) {
        logger.error("[Appointments] Keine gültige Auth-Session — Status-Update übersprungen");
        throw new AppointmentServiceError("Keine gültige Sitzung. Bitte erneut anmelden.", "UNAUTHORIZED");
    }

    let currentStatusQuery = sb.from("appointments").select("status").eq("id", appointmentId);

    if (practiceId) {
        currentStatusQuery = currentStatusQuery.eq("practice_id", practiceId);
    }

    const { data: currentAppointment, error: currentStatusError } = await currentStatusQuery.maybeSingle();

    if (currentStatusError) {
        logger.error(
            "[Appointments] Status vor Update laden fehlgeschlagen:",
            currentStatusError.message,
            currentStatusError.code,
        );
        throw new AppointmentServiceError(
            `Aktuellen Status laden fehlgeschlagen: ${currentStatusError.message}`,
            "APPOINTMENT_STATUS_READ_FAILED",
        );
    }

    let entity: "appointment" | "guest_appointment" = "appointment";
    let previousStatus = currentAppointment?.status as AppointmentStatus | undefined;

    if (!currentAppointment) {
        let currentGuestStatusQuery = sb
            .from("guest_appointments")
            .select("status")
            .eq("id", appointmentId);

        if (practiceId) {
            currentGuestStatusQuery = currentGuestStatusQuery.eq("practice_id", practiceId);
        }

        const { data: currentGuestAppointment, error: currentGuestStatusError } =
            await currentGuestStatusQuery.maybeSingle();

        if (currentGuestStatusError) {
            logger.error(
                "[Appointments] Gasttermin-Status vor Update laden fehlgeschlagen:",
                currentGuestStatusError.message,
                currentGuestStatusError.code,
            );
            throw new AppointmentServiceError(
                `Aktuellen Status laden fehlgeschlagen: ${currentGuestStatusError.message}`,
                "APPOINTMENT_STATUS_READ_FAILED",
            );
        }

        if (!currentGuestAppointment) {
            logger.error(
                "[Appointments] Termin fuer Statuswechsel nicht gefunden oder falsch gescoped:",
                appointmentId,
                practiceId ?? "no-practice-scope",
            );
            throw new AppointmentServiceError(
                "Termin nicht gefunden oder nicht fuer diese Praxis verfuegbar.",
                "APPOINTMENT_NOT_FOUND",
            );
        }

        entity = "guest_appointment";
        previousStatus = currentGuestAppointment.status as AppointmentStatus | undefined;
    }

    const timestamp = new Date().toISOString();
    let updatedAppointment: { id: string } | null = null;

    if (entity === "appointment") {
        let query = sb
            .from("appointments")
            .update({ status, updated_at: timestamp })
            .eq("id", appointmentId)
            .select("id");

        if (practiceId) {
            query = query.eq("practice_id", practiceId);
        }

        const { data, error } = await query.maybeSingle();

        if (error) {
            logger.error("[Appointments] Status Update fehlgeschlagen:", error.message, error.code);
            throw new Error(`Status aktualisieren fehlgeschlagen: ${error.message}`);
        }

        updatedAppointment = data as { id: string } | null;
    } else {
        let guestQuery = sb
            .from("guest_appointments")
            .update(buildGuestAppointmentStatusPatch(status, timestamp))
            .eq("id", appointmentId)
            .select("id");

        if (practiceId) {
            guestQuery = guestQuery.eq("practice_id", practiceId);
        }

        const { data, error } = await guestQuery.maybeSingle();

        if (error) {
            logger.error("[Appointments] Gasttermin-Status Update fehlgeschlagen:", error.message, error.code);
            throw new Error(`Status aktualisieren fehlgeschlagen: ${error.message}`);
        }

        updatedAppointment = data as { id: string } | null;
    }

    if (!updatedAppointment) {
        logger.error(
            "[Appointments] Status Update hat keinen Termin aktualisiert:",
            appointmentId,
            practiceId ?? "no-practice-scope",
        );
        throw new AppointmentServiceError(
            "Termin konnte fuer den Statuswechsel nicht aktualisiert werden.",
            "APPOINTMENT_STATUS_UPDATE_NOOP",
        );
    }

    if (entity === "appointment") {
        try {
            await syncWaitingRoomStatus(appointmentId, status);
        } catch (e) {
            logger.error("[Appointments] Strangler Fig Sync failed:", e);

            if (previousStatus && previousStatus !== status) {
                let rollbackQuery = sb
                    .from("appointments")
                    .update({ status: previousStatus, updated_at: new Date().toISOString() })
                    .eq("id", appointmentId);

                if (practiceId) {
                    rollbackQuery = rollbackQuery.eq("practice_id", practiceId);
                }

                const { error: rollbackError } = await rollbackQuery;

                if (rollbackError) {
                    logger.error(
                        "[Appointments] Rollback nach fehlgeschlagenem Waiting-Room-Sync fehlgeschlagen:",
                        rollbackError.message,
                        rollbackError.code,
                    );
                    throw new AppointmentServiceError(
                        "Statuswechsel fehlgeschlagen und Rollback konnte nicht abgeschlossen werden.",
                        "APPOINTMENT_STATUS_ROLLBACK_FAILED",
                    );
                }
            }

            throw new AppointmentServiceError(
                "Statuswechsel fehlgeschlagen, weil das Wartezimmer nicht synchronisiert werden konnte.",
                "WAITING_ROOM_SYNC_FAILED",
            );
        }
    }
}

function buildGuestAppointmentStatusPatch(
    status: AppointmentStatus,
    timestamp: string,
): Record<string, string | null> {
    const patch: Record<string, string | null> = {
        status,
        updated_at: timestamp,
    };

    if (status === "confirmed") {
        patch.confirmed_at = timestamp;
        patch.cancelled_at = null;
        patch.cancellation_reason = null;
    }

    if (status === "cancelled") {
        patch.cancelled_at = timestamp;
    }

    if (status === "arrived" || status === "checked_in" || status === "waiting") {
        patch.arrived_at = timestamp;
        patch.checked_in_by = "practice-web";
    }

    if (status === "in_treatment") {
        patch.treatment_started_at = timestamp;
    }

    return patch;
}

/**
 * Move / resize an appointment (Drag & Drop / Resize).
 * Tracks reschedule history via rescheduled_from_* fields.
 * Returns { success, conflicts? } — caller decides on conflict handling.
 */
export async function updateAppointmentTime(
    appointmentId: string,
    newStartTime: string,
    newEndTime: string,
    practiceId: string,
    /** Optional: Arzt/Ressource ändern bei Timeline-DnD */
    newDoctorId?: string,
): Promise<{ success: boolean; conflicts?: ScheduleItem[] }> {
    const sb = requireSupabase();
    await ensureValidSession();

    // 1. Conflict check (exclude self)
    const conflicts = await checkConflicts(practiceId, newStartTime, newEndTime, appointmentId);
    if (conflicts.length > 0) {
        return { success: false, conflicts };
    }

    // 2. Fetch old values for reschedule tracking
    const { data: old } = await sb
        .from("appointments")
        .select("start_time, end_time")
        .eq("id", appointmentId)
        .single();

    const durationMs = new Date(newEndTime).getTime() - new Date(newStartTime).getTime();
    const durationMinutes = Math.round(durationMs / 60_000);

    const updatePayload: Record<string, unknown> = {
        start_time: newStartTime,
        end_time: newEndTime,
        duration_minutes: durationMinutes,
        updated_at: new Date().toISOString(),
    };

    // Update doctor/resource if specified
    if (newDoctorId !== undefined) {
        updatePayload.doctor_id = newDoctorId;
    }

    // Track reschedule history (only if date/time actually changed)
    if (old?.start_time && old.start_time !== newStartTime) {
        const oldDate = new Date(old.start_time as string);
        updatePayload.rescheduled_from_date = oldDate.toISOString().slice(0, 10);
        updatePayload.rescheduled_from_time = oldDate.toTimeString().slice(0, 8);
        updatePayload.rescheduled_at = new Date().toISOString();
    }

    const { error } = await sb
        .from("appointments")
        .update(updatePayload)
        .eq("id", appointmentId)
        .eq("practice_id", practiceId);

    if (error) {
        logger.error("[Appointments] Time Update fehlgeschlagen:", error.message, error.code);
        throw new Error(`Termin verschieben fehlgeschlagen: ${error.message}`);
    }

    return { success: true };
}

/**
 * Create a new appointment
 */
export interface CreateAppointmentInput {
    practiceId: string;
    patientId: string;
    doctorId?: string;
    startTime: string; // ISO
    endTime: string; // ISO
    appointmentType: string;
    reason?: string;
    notes?: string;
    enforceNoConflicts?: boolean;
}

export interface DeterministicRecallBookingSlot {
    startTime: string;
    endTime: string;
    label: string;
    shortLabel: string;
    rationale: string;
}

interface FindDeterministicRecallBookingSlotOptions {
    durationMinutes?: number;
    now?: Date;
    searchBusinessDays?: number;
    maxResults?: number;
    conflictChecker?: typeof checkConflicts;
}

const RECALL_SLOT_CANDIDATES = [
    { hour: 9, minute: 0 },
    { hour: 9, minute: 30 },
    { hour: 10, minute: 0 },
    { hour: 10, minute: 30 },
    { hour: 11, minute: 0 },
    { hour: 11, minute: 30 },
    { hour: 14, minute: 0 },
    { hour: 14, minute: 30 },
    { hour: 15, minute: 0 },
    { hour: 15, minute: 30 },
] as const;

function isBusinessDay(date: Date): boolean {
    const weekday = date.getDay();
    return weekday !== 0 && weekday !== 6;
}

function atLocalMidnight(date: Date): Date {
    const copy = new Date(date);
    copy.setHours(0, 0, 0, 0);
    return copy;
}

function toLocalDate(dateLike: string): Date | null {
    if (!dateLike) return null;
    const parsed = new Date(`${dateLike}T00:00:00`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function nextBusinessDay(date: Date): Date {
    const next = atLocalMidnight(date);
    while (!isBusinessDay(next)) {
        next.setDate(next.getDate() + 1);
    }
    return next;
}

function buildRecallSlotLabel(date: Date): { label: string; shortLabel: string } {
    return {
        label: new Intl.DateTimeFormat("de-DE", {
            weekday: "long",
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        }).format(date),
        shortLabel: new Intl.DateTimeFormat("de-DE", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        }).format(date),
    };
}

export async function findDeterministicRecallBookingSlots(
    practiceId: string,
    recallDueDate: string,
    options: FindDeterministicRecallBookingSlotOptions = {},
): Promise<DeterministicRecallBookingSlot[]> {
    const durationMinutes = options.durationMinutes ?? 30;
    const searchBusinessDays = options.searchBusinessDays ?? 10;
    const maxResults = Math.max(1, options.maxResults ?? 3);
    const now = options.now ? new Date(options.now) : new Date();
    const conflictChecker = options.conflictChecker ?? checkConflicts;

    const today = atLocalMidnight(now);
    const dueDate = toLocalDate(recallDueDate);

    let searchDate = dueDate ? atLocalMidnight(dueDate) : today;
    let rationale =
        "Nächster konfliktfreier Recall-Termin ab dem hinterlegten Fälligkeitsdatum.";

    if (!dueDate) {
        searchDate = today;
        rationale =
            "Das Recall-Fälligkeitsdatum war nicht verwertbar. Daher wird der nächste konfliktfreie Werktag genutzt.";
    } else if (searchDate < today) {
        searchDate = new Date(today);
        searchDate.setDate(searchDate.getDate() + 1);
        rationale =
            "Der Recall ist überfällig. Daher wird der nächste konfliktfreie Werktag ab morgen vorgeschlagen.";
    }

    let businessDaysChecked = 0;
    let cursor = nextBusinessDay(searchDate);
    const slots: DeterministicRecallBookingSlot[] = [];

    while (businessDaysChecked < searchBusinessDays && slots.length < maxResults) {
        for (const candidate of RECALL_SLOT_CANDIDATES) {
            const start = new Date(cursor);
            start.setHours(candidate.hour, candidate.minute, 0, 0);

            if (start <= now) continue;

            const end = new Date(start.getTime() + durationMinutes * 60_000);
            const conflicts = await conflictChecker(
                practiceId,
                start.toISOString(),
                end.toISOString(),
            );

            if (conflicts.length === 0) {
                const { label, shortLabel } = buildRecallSlotLabel(start);
                slots.push({
                    startTime: start.toISOString(),
                    endTime: end.toISOString(),
                    label,
                    shortLabel,
                    rationale,
                });

                if (slots.length >= maxResults) {
                    return slots;
                }
            }
        }

        cursor = new Date(cursor);
        cursor.setDate(cursor.getDate() + 1);
        cursor = nextBusinessDay(cursor);
        businessDaysChecked += 1;
    }

    return slots;
}

export async function findDeterministicRecallBookingSlot(
    practiceId: string,
    recallDueDate: string,
    options: FindDeterministicRecallBookingSlotOptions = {},
): Promise<DeterministicRecallBookingSlot | null> {
    const [firstSlot] = await findDeterministicRecallBookingSlots(practiceId, recallDueDate, {
        ...options,
        maxResults: 1,
    });

    return firstSlot ?? null;
}

async function findDoctorIdForUser(
    sb: ReturnType<typeof requireSupabase>,
    practiceId: string,
    userId: string,
): Promise<string | null> {
    const { data } = await sb
        .from('doctors')
        .select('id')
        .eq('practice_id', practiceId)
        .eq('user_id', userId)
        .eq('is_active', true)
        .single();
    return data?.id ?? null;
}

async function ensurePatientPracticeLink(
    sb: ReturnType<typeof requireSupabase>,
    patientId: string,
    practiceId: string,
): Promise<void> {
    const timestamp = new Date().toISOString();

    try {
        const { error } = await sb.from("patient_practices").upsert(
            {
                patient_id: patientId,
                practice_id: practiceId,
                relationship_status: "active",
                connected_via: "booking",
                is_active: true,
                removed_at: null,
                last_appointment_at: timestamp,
                updated_at: timestamp,
            },
            {
                onConflict: "patient_id,practice_id",
                ignoreDuplicates: false,
            },
        );

        if (error) {
            logger.warn(
                "[Appointments] patient_practices Upsert Warnung:",
                error.message,
                error.code,
            );
        }
    } catch (err) {
        logger.warn("[Appointments] patient_practices Upsert Ausnahme:", err);
    }
}

async function rollbackAppointmentCreation(
    sb: ReturnType<typeof requireSupabase>,
    appointmentId: string,
): Promise<void> {
    try {
        const { error } = await sb.from("appointments").delete().eq("id", appointmentId);
        if (error) {
            logger.error(
                "[Appointments] Rollback nach Video-Bridge-Fehler fehlgeschlagen:",
                error.message,
                error.code,
            );
        }
    } catch (err) {
        logger.error("[Appointments] Rollback Ausnahme nach Video-Bridge-Fehler:", String(err));
    }
}

export async function createAppointment(input: CreateAppointmentInput): Promise<string> {
    const sb = requireSupabase();
    const isVideoAppointment = input.appointmentType === "Videosprechstunde";

    // Ensure valid auth session before write operation
    const token = await ensureValidSession();
    if (!token) {
        logger.error("[Appointments] Keine gültige Auth-Session — Termin-Anlage übersprungen");
        throw new AppointmentServiceError("Keine gültige Sitzung. Bitte erneut anmelden.", "UNAUTHORIZED");
    }

    if (input.enforceNoConflicts) {
        const conflicts = await checkConflicts(
            input.practiceId,
            input.startTime,
            input.endTime,
            undefined,
            { strict: true },
        );

        if (conflicts.length > 0) {
            throw new AppointmentServiceError(
                "Der vorgeschlagene Recall-Slot ist nicht mehr verfügbar.",
                "RECALL_NO_SLOT",
            );
        }
    }

    // Resolve doctor record ID from auth user ID
    let resolvedDoctorId: string | null = null;
    if (input.doctorId) {
        resolvedDoctorId = await findDoctorIdForUser(sb, input.practiceId, input.doctorId);
    }

    if (isVideoAppointment) {
        if (!input.doctorId) {
            throw new AppointmentServiceError(
                "Videosprechstunden brauchen einen zugewiesenen Arzt. Bitte zuerst einen Arzt auswählen.",
                "VIDEO_DOCTOR_REQUIRED",
            );
        }

        if (!resolvedDoctorId) {
            throw new AppointmentServiceError(
                "Videosprechstunden brauchen einen aktiv verknüpften Arzt im Praxisprofil.",
                "VIDEO_DOCTOR_REQUIRED",
            );
        }
    }

    const { data, error } = await sb
        .from("appointments")
        .insert({
            practice_id: input.practiceId,
            patient_id: input.patientId,
            doctor_id: resolvedDoctorId,
            start_time: input.startTime,
            end_time: input.endTime,
            appointment_type: input.appointmentType,
            status: "confirmed",
            reason: input.reason || null,
            notes: input.notes || null,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
        })
        .select("id")
        .single();

    if (error) {
        logger.error("[Appointments] Termin erstellen fehlgeschlagen:", error.message, error.code);
        throw new AppointmentServiceError(
            `Termin erstellen fehlgeschlagen: ${error.message}`,
            "CREATE_APPOINTMENT_FAILED",
        );
    }

    await ensurePatientPracticeLink(sb, input.patientId, input.practiceId);

    // Bridge: Video-Termin → video_sessions Row erstellen
    if (isVideoAppointment && input.doctorId) {
        try {
            const { error: vsError } = await sb
                .from('video_sessions')
                .insert({
                    appointment_id: data.id,
                    patient_id: input.patientId,
                    practice_id: input.practiceId,
                    doctor_id: resolvedDoctorId,
                    scheduled_at: input.startTime,
                    status: 'scheduled',
                });

            if (vsError) {
                await rollbackAppointmentCreation(sb, data.id as string);
                logger.error('[Appointments] Video-Session erstellen fehlgeschlagen:', vsError.message);
                throw new AppointmentServiceError(
                    'Videosprechstunde konnte nicht angelegt werden, weil die Video-Session nicht erstellt werden konnte.',
                    'VIDEO_SESSION_CREATE_FAILED',
                );
            }
        } catch (err) {
            if (err instanceof AppointmentServiceError) {
                throw err;
            }

            await rollbackAppointmentCreation(sb, data.id as string);
            logger.error('[Appointments] Video-Session Bridge Fehler:', String(err));
            throw new AppointmentServiceError(
                'Videosprechstunde konnte nicht angelegt werden, weil der Video-Startpfad fehlgeschlagen ist.',
                'VIDEO_SESSION_CREATE_FAILED',
            );
        }
    }

    return data.id as string;
}

/**
 * Check for conflicting appointments in a time range
 */
export async function checkConflicts(
    practiceId: string,
    startTime: string,
    endTime: string,
    excludeId?: string,
    options?: { strict?: boolean },
): Promise<ScheduleItem[]> {
    if (!isSupabaseConfigured()) return [];

    let query = supabase
        .from("appointments")
        .select(
            `
      id, patient_id, doctor_id, start_time, end_time,
      appointment_type, status, reason,
      patients!inner (first_name, last_name)
    `,
        )
        .eq("practice_id", practiceId)
        .not("status", "in", '("cancelled","no_show")')
        .lt("start_time", endTime)
        .gt("end_time", startTime);

    if (excludeId) {
        query = query.neq("id", excludeId);
    }

    const { data, error } = await query;
    if (error) {
        logger.error("[Appointments] Konfliktprüfung fehlgeschlagen:", error.message, error.code);
        if (options?.strict) {
            throw new Error(`Konfliktprüfung fehlgeschlagen: ${error.message}`);
        }
        return [];
    }

    return (data || []).map((row): ScheduleItem => {
        const r = row as Record<string, unknown>;
        return {
            id: r.id as string,
            patientId: r.patient_id as string,
            patientName: getPatientDisplayName(r),
            startTime: r.start_time as string,
            endTime: r.end_time as string,
            appointmentType: r.appointment_type as string,
            status: r.status as AppointmentStatus,
            reason: r.reason as string | null,
        };
    });
}

export async function getScheduleItemById(
    practiceId: string,
    appointmentId: string,
): Promise<ScheduleItem | null> {
    if (!isSupabaseConfigured()) return null;

    try {
        const { data } = await supabase
            .from("v_practice_calendar")
            .select("*")
            .eq("practice_id", practiceId)
            .eq("id", appointmentId)
            .single();

        if (data) {
            return mapScheduleItem(data as Record<string, unknown>);
        }
    } catch {
        // View nicht vorhanden, Fallback unten verwenden.
    }

    const { data, error } = await supabase
        .from("appointments")
        .select(
            `
      id, patient_id, doctor_id, start_time, end_time,
      appointment_type, status, reason, notes,
      booking_source, confirmation_code,
      patients(first_name, last_name)
    `,
        )
        .eq("practice_id", practiceId)
        .eq("id", appointmentId)
        .single();

    if (!error && data) {
        return mapScheduleItem(data as Record<string, unknown>);
    }

    try {
        const { data: guestData } = await supabase
            .from("guest_appointments")
            .select(
                `
      id,
      patient_first_name,
      patient_last_name,
      patient_phone,
      patient_email,
      start_time,
    end_time,
      service_name,
      status,
      notes,
      confirmation_code,
      insurance_type
    `,
            )
            .eq("practice_id", practiceId)
            .eq("id", appointmentId)
            .single();

        if (guestData) {
            const guestRow = guestData as Record<string, unknown>;
            const guestName = [guestRow.patient_first_name, guestRow.patient_last_name]
                .filter((value): value is string => typeof value === "string" && value.trim().length > 0)
                .join(" ")
                .trim();

            return {
                id: guestRow.id as string,
                patientId: "",
                patientName: guestName || "Gasttermin",
                startTime: (guestRow.start_time as string) || "",
                endTime: (guestRow.end_time as string) || (guestRow.start_time as string) || "",
                appointmentType: (guestRow.service_name as string) || "Termin",
                status: ((guestRow.status as AppointmentStatus | undefined) || "scheduled"),
                reason: null,
                notes: guestRow.notes as string | undefined,
                insuranceType: guestRow.insurance_type as string | undefined,
                bookingSource: "guest",
                confirmationCode: guestRow.confirmation_code as string | undefined,
                patientPhone: guestRow.patient_phone as string | undefined,
                patientEmail: guestRow.patient_email as string | undefined,
            };
        }
    } catch {
        // Canonical guest appointment lookup nicht verfügbar oder Datensatz fehlt.
    }

    try {
        const { data: legacyGuestData } = await supabase
            .from("guest_appointments")
            .select(
                `
      id,
      guest_name,
      guest_phone,
      guest_email,
      appointment_date,
      appointment_time,
      duration_minutes,
      status,
      confirmation_code,
      insurance_type,
      notes
    `,
            )
            .eq("practice_id", practiceId)
            .eq("id", appointmentId)
            .single();

        if (!legacyGuestData) return null;

        const legacyGuestRow = legacyGuestData as Record<string, unknown>;
        const startDate =
            typeof legacyGuestRow.appointment_date === "string" &&
            typeof legacyGuestRow.appointment_time === "string"
                ? `${legacyGuestRow.appointment_date}T${legacyGuestRow.appointment_time}`
                : "";
        const durationMinutes =
            typeof legacyGuestRow.duration_minutes === "number" ? legacyGuestRow.duration_minutes : 0;
        const endDate =
            startDate && durationMinutes > 0
                ? new Date(new Date(startDate).getTime() + durationMinutes * 60000).toISOString()
                : startDate;

        return {
            id: legacyGuestRow.id as string,
            patientId: "",
            patientName: (legacyGuestRow.guest_name as string) || "Gasttermin",
            startTime: startDate,
            endTime: endDate,
            appointmentType: "Termin",
            status: ((legacyGuestRow.status as AppointmentStatus | undefined) || "scheduled"),
            reason: null,
            notes: legacyGuestRow.notes as string | undefined,
            insuranceType: legacyGuestRow.insurance_type as string | undefined,
            bookingSource: "guest",
            confirmationCode: legacyGuestRow.confirmation_code as string | undefined,
            patientPhone: legacyGuestRow.guest_phone as string | undefined,
            patientEmail: legacyGuestRow.guest_email as string | undefined,
        };
    } catch {
        return null;
    }
}

// ── Reception: Today's appointments with patient+doctor joins ──

export interface ReceptionAppointment {
    id: string;
    start_time: string;
    appointment_type: string;
    status: string;
    patient: { id: string; first_name: string; last_name: string; insurance_type: string } | null;
    doctor: { id: string; title: string | null; first_name: string; last_name: string } | null;
}

/**
 * Get today's appointments for reception (with patient & doctor details).
 */
export async function getReceptionAppointments(
    practiceId: string,
): Promise<ReceptionAppointment[]> {
    if (!isSupabaseConfigured()) return [];

    const today = new Date().toISOString().split("T")[0];

    const { data, error } = await supabase
        .from("appointments")
        .select(
            "id, start_time, appointment_type, status, patient:patients(id, first_name, last_name, insurance_type), doctor:doctors(id, title, first_name, last_name)",
        )
        .eq("practice_id", practiceId)
        .gte("start_time", `${today}T00:00:00`)
        .lt("start_time", `${today}T23:59:59`)
        .order("start_time", { ascending: true });

    if (error) {
        throw new Error(`Empfangs-Termine laden fehlgeschlagen: ${error.message}`);
    }

    return (data as unknown as ReceptionAppointment[]) || [];
}

// --- Mapper ---

function mapScheduleItem(row: Record<string, unknown>): ScheduleItem {
    // Extract doctor name from joined data or flat fields
    let doctorName: string | undefined;
    const doctor = row.doctor as Record<string, unknown> | null | undefined;
    if (doctor && typeof doctor === "object") {
        const title = (doctor.title as string) || "";
        const first = (doctor.first_name as string) || "";
        const last = (doctor.last_name as string) || "";
        doctorName = [title, first, last].filter(Boolean).join(" ").trim() || undefined;
    }

    return {
        id: row.id as string,
        patientId: row.patient_id as string,
        patientName: getPatientDisplayName(row),
        startTime: row.start_time as string,
        endTime: row.end_time as string,
        appointmentType: (row.appointment_type as string) || "Termin",
        status: (row.status as AppointmentStatus) || "scheduled",
        reason: (row.reason as string) || null,
        serviceName: row.service_name as string | undefined,
        notes: row.notes as string | undefined,
        insuranceType: row.insurance_type as string | undefined,
        avatarUrl: row.avatar_url as string | undefined,
        arrivedAt: row.arrived_at as string | undefined,
        waitingMinutes: row.waiting_minutes as number | undefined,
        bookingSource: row.booking_source as ScheduleItem["bookingSource"],
        confirmationCode: row.confirmation_code as string | undefined,
        patientPhone: row.patient_phone as string | undefined,
        patientEmail: row.patient_email as string | undefined,
        seriesId: (row.series_id as string | null) ?? undefined,
        doctorId: (row.doctor_id as string) || undefined,
        doctorName,
    };
}

function getPatientDisplayName(row: Record<string, unknown>): string {
    const direct = row.patient_name;
    if (typeof direct === "string" && direct.trim().length > 0) {
        return direct;
    }

    const joined = row.patients;
    const patient = Array.isArray(joined)
        ? (joined[0] as Record<string, unknown> | undefined)
        : (joined as Record<string, unknown> | undefined);

    if (patient && typeof patient === "object") {
        const first = typeof patient.first_name === "string" ? patient.first_name : "";
        const last = typeof patient.last_name === "string" ? patient.last_name : "";
        const full = `${first} ${last}`.trim();
        if (full.length > 0) return full;
    }

    // Fallback: guest_patient_name or walk_in_name for walk-in/guest bookings
    const guestName = row.guest_patient_name;
    if (typeof guestName === "string" && guestName.trim().length > 0) {
        return guestName;
    }
    const walkInName = row.walk_in_name;
    if (typeof walkInName === "string" && walkInName.trim().length > 0) {
        return walkInName;
    }

    return "Patient";
}

// ── Resource Timeline ──────────────────────────────────────

const DOCTOR_COLORS = [
    "#6366f1",
    "#8b5cf6",
    "#ec4899",
    "#f97316",
    "#14b8a6",
    "#3b82f6",
    "#10b981",
    "#f59e0b",
    "#ef4444",
    "#06b6d4",
];

/**
 * Load resources (doctors + rooms) for the Ressourcen-Timeline.
 * Source of truth: practice_staff (role='doctor') for names/display.
 * doctors-Tabelle wird nur für ID-Mapping genutzt (appointments.doctor_id → doctors.id).
 */
export async function getCalendarResources(practiceId: string): Promise<CalendarResource[]> {
    if (!isSupabaseConfigured()) return [];
    const resources: CalendarResource[] = [];

    // 1. Ärzte aus practice_staff (echte Praxisdaten)
    try {
        const { data: staffData } = await supabase
            .from("practice_staff")
            .select("id, user_id, first_name, last_name, role")
            .eq("practice_id", practiceId)
            .eq("is_active", true)
            .eq("role", "doctor")
            .order("last_name");

        const activeStaff = staffData || [];

        // ID-Mapping: practice_staff.user_id → doctors.id (für appointments.doctor_id)
        const userIds = activeStaff.map((s) => s.user_id).filter(Boolean) as string[];
        const doctorIdMap = new Map<string, string>(); // user_id → doctors.id
        if (userIds.length > 0) {
            try {
                const { data: doctorRows } = await supabase
                    .from("doctors")
                    .select("id, user_id")
                    .in("user_id", userIds);
                (doctorRows || []).forEach((d) => {
                    if (d.user_id) doctorIdMap.set(d.user_id, d.id as string);
                });
            } catch {
                // doctors table optional
            }
        }

        activeStaff.forEach((s, i) => {
            // Appointments verwenden doctors.id → preferiere das als resourceId
            const resourceId = s.user_id
                ? (doctorIdMap.get(s.user_id) ?? s.id)
                : s.id;
            const name = [s.first_name, s.last_name].filter(Boolean).join(" ");
            resources.push({
                id: resourceId as string,
                type: "staff",
                name,
                color: DOCTOR_COLORS[i % DOCTOR_COLORS.length]!,
                role: "Arzt/Ärztin",
            });
        });
    } catch {
        // practice_staff might not exist
    }

    // 2. Rooms from practice_resources
    try {
        const { data } = await supabase
            .from("practice_resources")
            .select("id, name, color, icon, resource_type, is_active")
            .eq("practice_id", practiceId)
            .eq("is_active", true)
            .in("resource_type", ["room", "equipment"])
            .order("name");

        (data || []).forEach((r) => {
            resources.push({
                id: r.id,
                type: r.resource_type === "room" ? "room" : "equipment",
                name: r.name,
                color: r.color || "#94a3b8",
                icon: r.icon || undefined,
            });
        });
    } catch {
        // practice_resources might not exist
    }

    // Immer als letzte Row "Unzugewiesen" hinzufügen
    resources.push({
        id: "unassigned",
        type: "staff",
        name: "Ohne Zuweisung",
        color: "#94a3b8", // slate-400
        role: "Offene Termine",
    });

    return resources;
}

export interface CreateResourceInput {
    name: string;
    resource_type: "room" | "equipment";
    color?: string;
    icon?: string;
}

/**
 * Erstellt einen neuen Raum oder ein Gerät in practice_resources.
 */
export async function createPracticeResource(
    practiceId: string,
    input: CreateResourceInput,
): Promise<{ success: boolean; error?: string }> {
    if (!isSupabaseConfigured()) return { success: false, error: "Supabase nicht konfiguriert" };

    const { error } = await supabase.from("practice_resources").insert({
        practice_id: practiceId,
        name: input.name.trim(),
        resource_type: input.resource_type,
        color: input.color ?? "#6366f1",
        icon: input.icon ?? null,
        is_active: true,
    });

    if (error) return { success: false, error: error.message };
    return { success: true };
}

/**
 * Load schedule for resource timeline: appointments with doctor join.
 * Returns appointments enriched with doctor info for resource mapping.
 */
export async function getResourceSchedule(
    practiceId: string,
    date: string | Date,
): Promise<ResourceScheduleItem[]> {
    if (!isSupabaseConfigured()) return [];

    const dateObj = coerceCalendarDateInput(date);
    const dayStart = startOfDay(dateObj).toISOString();
    const dayEnd = endOfDay(dateObj).toISOString();

    // Fetch appointments with doctor join
    const { data, error } = await supabase
        .from("appointments")
        .select(
            `id, patient_id, doctor_id, start_time, end_time, doctor_id,
             appointment_type, status, reason, notes, assigned_resource,
             patients(first_name, last_name),
             doctor:doctors(id, title, first_name, last_name)`,
        )
        .eq("practice_id", practiceId)
        .gte("start_time", dayStart)
        .lte("start_time", dayEnd)
        .not("status", "eq", "cancelled")
        .order("start_time", { ascending: true });

    if (error) {
        logger.error("[Resource Schedule] Laden fehlgeschlagen:", error.message);
        return [];
    }

    const items: ResourceScheduleItem[] = [];
    for (const row of data || []) {
        const r = row as Record<string, unknown>;
        const base = mapScheduleItem(r);
        const doctorId = r.doctor_id as string | null;

        // Map to doctor resource or unassigned
        if (doctorId) {
            items.push({ ...base, resourceId: doctorId });
        } else {
            items.push({ ...base, resourceId: "unassigned" });
        }

        // Also map to room resource if assigned
        const assignedRoom = r.assigned_resource as string | null;
        if (assignedRoom) {
            items.push({ ...base, resourceId: assignedRoom, id: `${base.id}_room` });
        }
    }

    return items;
}
